<template>

  <v-container fluid class="page page-receiver-org-list">

    <!-- Breadcrumb & Actions -->
    <v-row class="mt-3 mb-6">
      <v-col sm="8" class="d-flex align-center justify-start page-breadcrumb-new">
        <!-- (breadcrumb) -->
        <v-icon small class="mr-2">mdi-account-circle-outline</v-icon>
        <div>Receiver Orgs</div>
      </v-col>
      <v-col sm="4" align="right">
        <!-- (register receiver org) -->
        <v-btn color="light-blue darken-1" @click="isRegisterOrgDialogOpen = true" v-if="$root.activeUserHasRole('sys-admin')">
          Register Receiver Org
        </v-btn>
      </v-col>
    </v-row>

    <!----------------------->
    <!-- Receiver Org List -->
    <!----------------------->
    <v-container fluid class="mt-3 mb-6 page-content">
      <v-row class="mb-4">
        <v-col class="pt-2">
          <div class="page-title">
            <v-icon class="pr-1">mdi-account-circle-outline</v-icon>
            Receiver Orgs
          </div>
        </v-col>
      </v-row>

      <v-data-table
          :headers="headers"
          :items="receiverOrgItems"
          :search="search"
          :options.sync="dataOptions"
          :footer-props="paginationOptions"
          :loading="isDataLoading"
          :server-items-length="totalItems"
          @click:row="goToItemPage">

        <!-- org name -->
        <template v-slot:[`item.name`]="{ item }">
          <span style="font-size:1.14em">
            <router-link :to="generateReceiverPageLink(item)">
              {{ item.name }}
            </router-link>
          </span>
        </template>

        <!-- receiver locations -->
        <template v-slot:item.receiver_locations="{ item }">
          <span>{{ item.receiver_locations.length }}</span>
        </template>

        <!-- created date -->
        <template v-slot:item.created_at="{ item }">
          <span>{{ formatDateTime(item.created_at) }}</span>
        </template>

        <!-- status -->
        <template v-slot:[`item.org_status`]="{ item }">
          <v-chip label outlined :class="item.org_status" v-if="item.org_status">
            {{ item.org_status.toUpperCase() }}
          </v-chip>
        </template>
      </v-data-table>
    </v-container>

    <!------------------------->
    <!-- Register Org Dialog -->
    <!------------------------->
    <v-dialog persistent max-width="850" v-model="isRegisterOrgDialogOpen">
      <v-card class="register-org-form">
        <v-card-text class="pb-0">
          <register-org-form
              org_type_code="receiver"
              v-on:close="closeRegisterOrgDialog" />
        </v-card-text>
      </v-card>
    </v-dialog>

  </v-container>

</template>

<script>
import RegisterOrgForm from '@/components/orgs/RegisterOrgForm'

export default {
  name: 'ReceiverOrgListPage',

  components: {
    RegisterOrgForm
  },

  data () {
    return {
      // -------------- Receiver org list management
      headers: [
        { text: 'Name', width: '25%', value: 'name' },
        { text: 'Owner', width: '15%', value: 'registration_code.email' },
        { text: 'Locations', width: '80px', align: 'center', value: 'receiver_locations' },
        { text: 'Joined RouteLeaf', width: '20%', value: 'created_at' },
        { text: 'Status', width: '10%', align: 'center', value: 'org_status' }
      ],
      isDataLoading: false,
      dataLoadingError: null,
      receiverOrgData: null,
      paginationOptions: {
        'items-per-page-options': [10, 25, 50, 100]
      },
      dataOptions: {
        itemsPerPage: 10,
        // NOTE - Currently enabled for single field sort only !!!
        sortBy: ['name'],
        sortDesc: [false]
      },
      search: null,
      // -------------- Register org management
      isRegisterOrgDialogOpen: false
    }
  },

  computed: {
    receiverOrgItems () {
      return (this.receiverOrgData) ? this.receiverOrgData.items : []
    },
    dataMeta () {
      return (this.receiverOrgData) ? this.receiverOrgData.meta : {}
    },
    totalItems () {
      return this.dataMeta.total_items || 0
    }
  },

  watch: {
    // Observe changes invoked by the data-table component (e.g. pagination, sorting)
    dataOptions: {
      handler () {
        this.queryData()
      },
      deep: true
    }
    // Observe changes to the search field
    // search: function (updated) {
    //   this.queryData()
    // }
  },

  methods: {
    // Display a date/time
    formatDateTime (date) {
      return (date) ? this.$root.utils.format(date, 'YYYY/MM/DD h:mm a') : ''
    },

    // Generate link data for receiver org page
    generateReceiverPageLink (item) {
      if (item && item.model) {
        return {
          name: 'receiver-org-item',
          params: { orgId: item.id }
        }
      } else {
        return ''
      }
    },

    // Navigate to receiver org page
    goToItemPage (item) {
      if (item && item.model) {
        this.$root.goTo('receiver-org-item', { orgId: item.id })
      } else {
        console.error('Invalid item data. Cannot invoke item page.')
      }
    },

    // Close register org dialog (and determine if we need to refresh list)
    closeRegisterOrgDialog (data = {}) {
      this.isRegisterOrgDialogOpen = false
      if (data.registered) this.queryData()
    },

    // Perform query of receiver orgs
    async queryData () {
      this.isDataLoading = true

      try {
        // Build sort
        // NOTE - Currently enabled for single field sort only !!!
        let orderBy = null
        const sortBy = this.dataOptions.sortBy
        const sortDesc = this.dataOptions.sortDesc
        if (sortBy && sortBy.length > 0) {
          const isDesc = (sortDesc && sortDesc.length > 0) ? sortDesc[0] : false
          const prefix = (isDesc) ? '-' : ''
          orderBy = `${prefix}${sortBy[0]}`
        }

        // Build pagination
        const page = this.dataOptions.page || 1
        const limit = this.dataOptions.itemsPerPage
        const skip = (page === 1) ? 0 : (page - 1) * limit

        // Build request input
        const params = {
          skip,
          limit,
          with: 'registration_code,receiver_locations'
        }

        // Apply sorting
        if (orderBy) params.order_by = orderBy

        // Apply search query (for name only)
        if (this.search) params['name.contains'] = this.search

        // Retrieve results
        this.receiverOrgData = await this.$store.dispatch('sh/dataApi', {
          method: 'get',
          uri: 'receiver-orgs',
          opts: { params }
        })
      } catch (err) {
        this.dataLoadingError = err.message || err.text || err
      } finally {
        this.isDataLoading = false
      }
    }
  }
}
</script>

<style>
  .page-receiver-org-list .theme--dark.v-data-table {
    cursor: pointer;
  }

  .page-receiver-org-list .v-data-table > .v-data-table__wrapper > table > tbody > tr > td,
  .page-receiver-org-list .v-data-table > .v-data-table__wrapper > table > thead > tr > td,
  .page-receiver-org-list .v-data-table > .v-data-table__wrapper > table > tfoot > tr > td {
    height: 65px;
  }

  .page-receiver-org-list .v-data-table a {
    text-decoration: none;
  }
  .page-receiver-org-list .v-data-table a:hover {
    text-decoration: underline;
  }

  .register-org-form {
    background-color: #121212 !important;
  }
</style>
